// <purpose>The draft area of a triage ticket's KI-Vorschlag section (Part C, 2026-09-07):
// the information panel on top, then whatever the row's draft slots amount to — a CARD per
// composed draft, a fixed-height STRIP per slot that is offered but not composed yet, or — on an
// older row with neither and no Begründung above it — one plain „Kein Entwurf vorhanden" line. One
// component owns all three shapes, so a row can never render two of them at once or none of them
// by omission.
// The draft slots are walked as ONE ordered list (2026-09-08) — the model's own order, which is
// the KI's proposal first and then the tenant's served axis order — each slot rendering its own
// card / strip / nothing in its own place, never all cards followed by all strips, which let the
// kinds swap positions as drafts arrived. The receipt („Empfangsbestätigung") joined that walk on
// 2026-09-08 and needed NO rule of its own here, which is what the walk is for: it is a slot like
// the others, offered by its own posture and composed by its own strip. Its text is a fixed
// notice, not a draft — so it renders as plain text with nothing styled around it, and it is
// copy-only and non-editable.
// Since 2026-09-17 the receipt normally needs no strip at all: it is STATELESS (composing it writes
// no draft row), so the detail read composes it and carries it (`TicketDetail.receipt`), the fold
// fills the ack slot from THAT, and this walk paints a card the clerk never had to press for. The
// strip survives for a core that serves no receipt — and its success now reveals the card from the
// render ANSWER, because waiting for a draft row that is never written is what turned a 200 into
// „Entwurf konnte nicht erzeugt werden" at the 30s bound.
// A card is built on a ruled TWO-LINE header (2026-09-09): an EMPTY first line, kept for the space
// alone (walk observation B retired the subject subtitle that stood there — it repeated the detail
// pane's own h2 once per card), then the kind with this card's one control anchored right.
// A card is NOT a stage (walk observation C, same day, reversing the shape this file shipped that
// morning): the per-card Maximieren is gone — an enlarged view exists to compare the reply against
// the forward, which is a SECTION's job and never one card's — and the editor came off the stage
// with it. Bearbeiten swaps the card's BODY for a textarea in place, in the same slot, with
// [Speichern] [Abbrechen] standing where the footer's Kopieren sits. What that removed is entirely
// machinery: the card stage id, the hand-back to the section stage underneath, and the "who opened
// this stage" bookkeeping. What it did not touch is every semantic rule below.</purpose>
// <dependencies>services/draftClaims (buildDraftModel's DraftModel is passed IN — the host folds
// it once per render and the bottom bar reads the same object; draftKindLabel/claimStateLabel are
// the German naming home) · shared (Spinner, SkeletonBlock,
// header icon chrome, the house redaction treatment) · theme tokens. NOT CompareStage: the section
// around this area is staged by its host, and no card here opens an overlay of its own.</dependencies>
// <constraints>UI Layout Stability is the whole point of the shape rules, and it reaches the
// LOADING window too: while cm.configLoading the slot order is not yet knowable, so the area
// shimmers its reserved canvas rather than painting cards it would have to re-order on settle
// (2026-09-08). the wrapper reserves
// the ~140px canvas a composed draft occupies, a strip is exactly 44px tall in its offer and
// failed states and grows ONLY at the press itself — reserve-at-press (Part C wave 2, 2026-09-09,
// adopted lean): the growth is the press's OWN user-initiated gesture, into the card's letter-box
// canvas, in the SAME render tick as the click, never something waited on — and every card mounts
// its FOOTER at a fixed height whether or not a claim exists — so a draft arriving, a claim
// landing, or a composition failing never moves anything already on screen. Kopieren confirms
// itself by swapping its own label inside a reserved width, never by growing a second element into
// the row it sits on.
// Draft text is PLAIN text, never HTML: it reaches the
// DOM as a text node, and the one exception (a redacted draft) goes through the same
// escape-then-style pipeline every other plain-text surface uses. Internal wording only, never
// customer-facing; no emojis (the strip's chevron is drawn, not typed).
// A strip is a WRITE affordance, so it is mounted only for the three statuses where a triage
// decision still exists — the Archiv shows the same cards and panel read-only. Bearbeiten obeys
// that same rule for the same reason (2026-09-09) — and since the walk round it is the only
// per-card control there is. And because a
// pressed strip UNMOUNTS on success — after standing as the reserved skeleton the whole time, it
// never disappears before the card is ready to take its place — the card that replaces it takes
// the focus the strip gave up: focus follows the thing that arrived, never falls to the document
// body.
// The edit BUFFER is local to the card and the in-flight flag is the HOST's: the buffer is
// unconfirmed typing and nothing above the card may decide when it ends, while the flag must
// survive anything that rebuilds this subtree. The one exit that ends an edit without the clerk
// choosing to is a STALE save, and it RETAINS the words (see `unsaved`); a ticket swap is the only
// other thing that may drop them, because a buffer belongs to the row it was typed against.
// The editor and Kopieren share one footer slot and one register (cardButtonStyle) — a card's
// buttons are card-sized; approval language lives in the bottom bar and nowhere else.</constraints>
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ClerkTrace } from './ClerkTrace';
import { DraftClaim, DraftKind, ScaffoldData, ScaffoldSegment, TicketDetail } from '../types';
import type {
  DraftEditableKind, DraftModel, DraftSlot, DraftSlotKind,
} from '../services/draftClaims';
import {
  candidateForKind, claimFaceKey, draftKindLabel, draftKindOfferLabel, isDraftDecisionKind, isDraftEditableKind,
  isTriageDecidable, parseScaffoldJson,
} from '../services/draftClaims';
import { onDisclosureChanged, readDisclosed, setDisclosed } from '../services/draftDisclosure';
import type { FaceFn } from '../services/categoryMeta';
import { isStaleSuggestionError } from '../services/staleSuggestion';
import { useKlaerfallDraft } from '../hooks/useKlaerfallDraft';
import KlaerfallEditor from './KlaerfallEditor';
import {
  PencilIcon,
  Spinner, SkeletonBlock, styleRedactionTokens, escapeHtmlText, ServedFace,
} from './shared';
import { pillFace, pillStyle } from './SecondaryAxisPills';
import { InfoTooltip } from './InfoTooltip';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { theme } from '../theme';

export interface TriageDraftAreaProps {
  ticket: TicketDetail;
  /** Folded ONCE by the host (see TicketDetail) — the bottom bar reads this same object. */
  model: DraftModel;
  /**
   * Composes ONE slot's draft on demand. The answer carries the COMPOSITION, not just a verdict
   * (2026-09-17): `text` (with its `renderSource` and raw `scaffoldJson` where the server describes
   * them) is what the pressed strip reveals its card from. That is not an optimization — the
   * receipt is stateless, so a success that waited for a refetched draft slot waited for a row that
   * is never written, and the wait timed out into the refusal on a composition that had succeeded.
   * A success WITHOUT text is still honoured the old way: the host refetched, and the card arrives
   * with the model.
   */
  onRender: (kind: DraftSlotKind) => Promise<{
    success: boolean; text?: string; renderSource?: string; scaffoldJson?: string | null;
    error?: string; refused?: boolean; stale?: boolean;
  }>;
  /**
   * Absent ⇒ the cards offer no Kopieren; the host wires it when copying is available.
   * Resolves to whether the CLIPBOARD write happened (2026-09-07) — the card confirms itself on
   * that boolean alone, so a refused write leaves the button saying Kopieren and the host's own
   * banner carries the reason. Whether a claim was recorded is the host's business, not the
   * button's: a copy that reached the clipboard is a copy.
   */
  onCopyDraft?: (kind: DraftSlotKind, text: string) => Promise<boolean>;
  /**
   * The letter the host shows (2026-10-30): a row with a reply AND a forward reads one at a time,
   * chosen by the switch above the letter. Every other composed decision letter is hidden — kept
   * mounted, so its fill state and edit buffer survive the switch. Strips and the receipt stay.
   * Absent ⇒ every slot shows, as it always did.
   */
  shownKind?: DraftSlotKind | null;
  /** A resting letter was opened from its strip: the host shows it (the switch follows). */
  onShowKind?: (kind: DraftSlotKind) => void;
  /**
   * Absent ⇒ no card offers Bearbeiten. The host owns the mutation (it holds the suggestion row's
   * id and the stale handler); the card owns the buffer and states a refusal in place.
   */
  onEditDraft?: (
    kind: DraftEditableKind, text: string, scaffoldSegments?: ScaffoldSegment[],
  ) => Promise<{ success: boolean; error?: string; stale?: boolean }>;
  /**
   * The kind whose write is on the wire, held by the HOST (fix round 1, 2026-09-09) — the same
   * flag the bottom bar reads, so a save and a claim on one kind can never both be in flight.
   */
  busyKind?: DraftKind | null;
  /**
   * Whether the hold has lasted long enough to be WORN (walk fix round 4, 2026-09-09). `busyKind`
   * refuses immediately; this says whether to look refused. A fast round trip washed every face out
   * and back in inside a couple of frames, which reads as a glitch rather than as a state.
   * Absent ⇒ never dim on account of the hold — refusals stay correct either way.
   */
  holdVisible?: boolean;
}

// The statuses in which composing another draft is still a decision the clerk can make. Every
// other status — archived above all — renders the same cards and panel READ-ONLY: an offer strip
// there would be a write affordance on a settled ticket, and the Archiv host owes no onRender.
// The SET itself moved to services/draftClaims on 2026-09-09 (walk fix round 6): the hosts' copy
// handlers were answering the same question with a narrower list of their own, so it is declared
// once in the vocabulary module and read here.

// The two halves of ONE refusal, spelled beside each other so they cannot drift apart: the strip
// states the fact inside its own 44px, and the host toast (raised by TicketDetail, which owns the
// call) names the cause the clerk can act on. Not two independent messages about two events.
const REFUSAL_TEXT = 'Entwurf konnte nicht erzeugt werden';
export const DRAFT_REFUSAL_TOAST = 'Der Entwurf konnte nicht erzeugt werden — Vorlage fehlt oder ist unvollständig.';
// How long a card states that its text went to the clipboard. Same 1.5 s cadence as the letter
// flow's Kopieren, so the two surfaces confirm a copy identically.
const COPY_FEEDBACK_MS = 1500;
// The Kopieren button's reserved width (2026-09-07). Both labels are the same length, but the
// width is pinned rather than measured: a swapping label must never be able to resize the footer.
const COPY_BUTTON_WIDTH = 58;
// The save pair's reserved widths (walk observation C, 2026-09-09). Speichern's box carries the
// 12px busy slot beside its label, which is why it is the wider of the two; both are pinned for the
// same reason Kopieren's is — a button that resizes when it becomes busy moves the button beside it.
const SAVE_BUTTON_WIDTH = 92;
const CANCEL_BUTTON_WIDTH = 76;
// The editor's floor. A body shorter than this opens into a box the clerk can actually write a
// paragraph in; anything taller keeps its own height (see editBoxHeight), so the swap never shrinks
// a card under the text being rewritten.
const EDIT_BOX_MIN_HEIGHT = 160;
// How long a strip states its own failure before returning to the offer. Long enough to read,
// short enough that the offer comes back without the clerk having to do anything.
const STRIP_MESSAGE_MS = 4000;
const STRIP_HEIGHT = 44;
// The bound on the reserved wait (Part C wave 2, 2026-09-09, folds the T2 "unbounded spinner"
// minor) — see DraftKindStrip below. A composition whose promise resolves success but whose
// refetch never lands the slot, or one that never settles at all, must not leave a clerk staring
// at a skeleton forever; 30s is long enough that no ordinary composition ever sees it.
const STRIP_TIMEOUT_MS = 30000;
// The card's own letter-box canvas — the same ~140px TicketDetail's KI-Vorschlag section reserves
// for "one suggestion height" and this file's own wrapper (below) reserves for "a composed draft
// occupies". A press claims this height IMMEDIATELY (reserve-at-press, Part C wave 2, 2026-09-09),
// so the arriving card never has to grow the row a second time for a typical draft — a draft whose
// text is genuinely longer than one letter box still grows past this once it lands, but that is
// the SAME user-initiated gesture as the press realized a beat later, not a second unannounced jump.
const CARD_MIN_HEIGHT = 140;
// The reserved skeleton's own line, beside the spinner — states what is happening, not just that
// something is (the strip's old busy state said nothing at all).
const RESERVE_LABEL = 'Entwurf wird erzeugt…';

// The empty letter, as ONE array whose identity never changes. Every card calls the fill-in hook
// (rules of hooks), and handing a card with nothing to fill a fresh `[]` on each paint would
// rebuild that hook's callbacks for every draft on screen, every render.
const NO_SEGMENTS: ScaffoldSegment[] = [];

// The house letter box — same tokens as TicketDetail's rendered suggestion, so a triage draft and
// a letter draft read as the same object on screen.
const letterBoxStyle: React.CSSProperties = {
  padding: theme.spacing.md,
  backgroundColor: theme.colors.bgPage,
  borderRadius: theme.borderRadius.md,
  border: `1px solid ${theme.colors.borderLight}`,
  fontSize: 13, lineHeight: 1.6, color: theme.colors.navyMid,
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};

// The card's footer row, mirroring the letter flow's own (TicketDetail's rendered-suggestion
// footer, 2026-09-08): the 11px meta register on the left, the copy affordance pushed right. It is
// mounted on EVERY card at this height, claim or no claim, so a claim landing never grows the card.
const CARD_FOOTER_HEIGHT = 24;
const cardFooterStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: theme.spacing.xs,
  minHeight: CARD_FOOTER_HEIGHT, marginTop: theme.spacing.xs, fontSize: 11,
};

// The claim pills speak the house INFO register — what a pill says ABOUT a draft rather than how
// it is classified (pill house rule, operator ruling 2026-09-08), the same register the list row's
// own chips draw from, so a claim reads identically wherever it is shown.
// flexShrink 0 (T2 parked minor, closed 2026-09-09): a footer can carry two of these at once
// (the claim state, „bearbeitet") beside a Kopieren whose width is reserved. Left to
// shrink, each pill's width became a function of how many pills stood beside it — so a claim
// landing re-flowed the two already there, which is precisely what the fixed-height footer exists
// to prevent.
const claimPillStyle: React.CSSProperties = { ...pillStyle('info', 'row'), fontWeight: 600, flexShrink: 0 };

/**
 * The card's own small-button register — ONE spelling for every button a card's footer can carry
 * (Kopieren, and the Speichern/Abbrechen pair that stands in its slot while the draft is being
 * rewritten). Deliberately NOT the bottom bar's ActionButton: approval language lives in the bottom
 * bar (the #114 ruling), and a big navy button inside a card would read as a decision about the
 * ticket rather than about this box. They share a height because the footer's is fixed, and differ
 * only in the reserved width and in which of them leads.
 *   lead  — filled navy: the one action the clerk is here to take.
 *   quiet — navy outline on white: live, and secondary to the lead beside it. NOT the bar's `off`
 *           face, which is the ruled not-clickable look.
 *   held  — the 0.5 fade both faces take while a write is on the wire, because disabled has to
 *           READ disabled (fix round 1's own finding, kept).
 */
const cardButtonStyle = (lead: boolean, width: number, held: boolean): React.CSSProperties => ({
  padding: '2px 10px', fontSize: 11, fontWeight: 600,
  fontFamily: theme.font.family,
  // Reserved box: a label (or a busy icon) changes inside it, the button never resizes.
  minWidth: width, textAlign: 'center',
  color: lead ? theme.colors.white : theme.colors.navy,
  backgroundColor: lead ? theme.colors.navy : 'transparent',
  border: `1px solid ${theme.colors.navy}`,
  borderRadius: theme.borderRadius.sm,
  opacity: held ? 0.5 : 1,
  cursor: held ? 'not-allowed' : 'pointer',
  transition: 'color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease',
});

// The card header's TITLE line, reserved whether or not this host wires any control into it — the
// kind stands there alone otherwise, and the icons arriving must not change the line's height.
const CARD_HEADER_LINE_HEIGHT = 24;
// The emptied FIRST line (walk observation B, 2026-09-09). The subject subtitle that stood here is
// gone; the operator kept the LINE for the space it gives between the section above and the card,
// so it is a reserved height with nothing in it — deliberately the same height it had, since the
// gap the operator approved in the walk is the one they were looking at.
const CARD_HEADER_SPACER_HEIGHT = CARD_HEADER_LINE_HEIGHT;
/**
 * THE CARD TITLE ROW's full height — the line itself plus the gap its header holds under it, i.e.
 * exactly the two figures the card lays out with, summed here rather than restated, so the two can
 * never drift into a misalignment nobody can see the cause of.
 *
 * Exported because a column that is NOT a card has to reserve it (operator ruling 2026-09-18): on
 * a maximized stage holding two or three slots side by side, a column still showing its 44px strip
 * used to start level with the neighbouring card's TITLE, reading as a heading rather than as the
 * letter it stands for. The strip's column carries this above itself instead, so the strip begins
 * where the card's letter box does — and when the strip expands, its own title row lands in the
 * reserved space, both titles line up, and nothing below either column moves.
 */
export const DRAFT_CARD_TITLE_ROW_HEIGHT = CARD_HEADER_LINE_HEIGHT + theme.spacing.xs;

/**
 * The blank line between one slot and whatever is above it. It belongs to the SLOT, not to the
 * card (walk fix round 4, 2026-09-09): it lived in the card header alone, so an offer strip
 * reserved its 44px and nothing else — and the instant a clerk pressed one, the arriving card
 * brought this line with it and pushed everything below the slot down by exactly its height. That
 * is a growth the clerk did not ask for, riding along with one they did.
 * Rendered by the card AND the strip, so the space is standing before the press and the swap
 * changes only what is under it.
 */
const SlotSeparator: React.FC<{ kind: DraftSlotKind }> = ({ kind }) => (
  <div
    data-testid={`draft-separator-${kind}`}
    // Decorative by construction — there is nothing here to read, so nothing to announce.
    aria-hidden="true"
    style={{ height: CARD_HEADER_SPACER_HEIGHT }}
  />
);

const ChevronIcon: React.FC = () => (
  <svg aria-hidden="true" focusable="false" width={14} height={14} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

// ─── A composed draft ───────────────────────────────────

const DraftKindCard: React.FC<{
  slot: DraftSlot;
  /** The tenant's served `draft_kind` vocabulary (part 4c, 2026-09-27) — the host reads it once
   *  (cm.face), the whole draft area shares it, and the parent gates this card behind
   *  `cm.configLoading` so a raw key is never painted here. */
  face: FaceFn;
  /** Identity of the row the card is showing; a retained edit buffer is dropped when it changes. */
  ticketUuid?: string;
  /**
   * The SUGGESTION-attempt key this row's fills belong to (`sug-<id>`), which each card extends
   * with its own kind. Keyed by the attempt rather than the ticket for the same reason the letter
   * flow's is: a reanalysis is a new attempt under the same uuid and must open a clean letter, not
   * the previous attempt's half-closed one.
   */
  draftKeyBase: string;
  /**
   * The row's served primary-axis value — the handling. Only the gap chips read it, and only to be
   * DRAWN in the handling's own served hue (the editor resolves it through the same
   * resolveColor/marktprozessTint pair the VNB letters use). Never restated as text here: the
   * ticket header names the axis, and the panel above states the classification.
   */
  marktprozess: string;
  targetUnit?: string | null;
  onCopyDraft?: (kind: DraftSlotKind, text: string) => Promise<boolean>;
  /** This card is the draft that just arrived from a pressed strip — it takes the focus. */
  claimFocus?: boolean;
  onFocusClaimed?: () => void;
  onEditDraft?: TriageDraftAreaProps['onEditDraft'];
  /** This card's own write is on the wire. */
  saving?: boolean;
  /**
   * ANOTHER kind's draft ACTION is on the wire (round-2 review I2; wording corrected in round 3 —
   * the flag is not writes-only, a download is a read that holds it too, and so is a copy).
   * `busyDraftKind` is one nullable kind, so it can only describe one action at a time — and two
   * editors can now be open at once. A second action starting while the first is out would
   * overwrite the flag, and the first answering would clear it and re-arm a control for something
   * still in flight. So while any draft action is out, every OTHER card's save is refused too, and
   * says so.
   */
  otherSaving?: boolean;
  /** The hold is old enough to be worn — see TriageDraftAreaProps.holdVisible. */
  holdVisible?: boolean;
}> = ({
  slot, face, ticketUuid, draftKeyBase, marktprozess, targetUnit, onCopyDraft, claimFocus,
  onFocusClaimed, onEditDraft, saving, otherSaving, holdVisible,
}) => {
  const text = slot.text ?? '';
  const kindLabel = draftKindLabel(slot.kind, face, targetUnit);
  const claimKey = claimFaceKey(slot.claim);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyRef = useRef<HTMLButtonElement | null>(null);
  // ── The letter, and what is still missing from it ─────
  // A draft the composer built from SEGMENTS can carry gaps: wherever a system fact belongs that
  // the model was forbidden to invent, it emitted a `[Slot]` and the composer turned that into an
  // open slot segment. Such a letter is filled in here exactly as a VNB Klärfall is — the same
  // KlaerfallEditor, the same numbered checklist, the same keyboard — because it is the same
  // gesture and a second fill-in surface would be a second set of rules to learn.
  // Read off the SERVED status, never off the live fill: whether this card is a fill-in letter is
  // therefore fixed for as long as the row is on screen, so the pieces can neither arrive nor
  // vanish under the clerk as the gaps close (the layout law), and closing the last one changes
  // only what the checklist rows themselves look like.
  const segments = slot.scaffold?.segments ?? NO_SEGMENTS;
  // Whether this card is a FILL-IN letter at all — does it have slot segments, open or not. NOT
  // "does it have open ones": a confirmed fill is written through and the row refetched, so the
  // served statuses turn `filled` under a card the clerk is still reading. Keyed to the open ones,
  // the whole editor would vanish at the moment the last gap closed, taking the clerk's own
  // checklist with it. Keyed to the segments, closing a gap changes only that row's own look —
  // which is what the layout law asks of a fill.
  const hasSlots = segments.some(seg => seg.kind === 'slot');
  /**
   * The fill, WRITTEN THROUGH (Task 6b). `useKlaerfallDraft` is optimistic-then-rollback: it shows
   * the confirmation at once, calls this, and re-opens the slot if it answers false — the same
   * contract the letter flow's own fill runs on, so both roles behave identically under a refused
   * or lost write. The endpoint is the per-kind draft PATCH, carrying the letter's segments beside
   * the assembled text; the host refetches on success, which makes the SERVED scaffold the source
   * of truth again and is what the bar's own refusal reads.
   * A host that wires no rewrite (the Archiv) answers false, so a settled row cannot be filled in.
   */
  const persistFill = useCallback(async (text: string, scaffoldSegments?: ScaffoldSegment[]) => {
    // Narrowing, not a gate: a scaffold-backed letter is always one of the editable kinds (the
    // receipt carries no segments), so this is the type system carrying that fact into the call.
    if (!onEditDraft || !isDraftEditableKind(slot.kind)) return false;
    const res = await onEditDraft(slot.kind, text, scaffoldSegments);
    return !!res?.success;
  }, [onEditDraft, slot.kind]);
  const fill = useKlaerfallDraft(
    `${draftKeyBase}-${slot.kind}`, hasSlots ? segments : NO_SEGMENTS, persistFill);
  // Zero on every plain-text draft, so nothing below needs to ask whether this card has a letter.
  const openCount = hasSlots ? fill.openCount : 0;
  const gapsOpen = openCount > 0;
  // The ONE sentence that says what is missing, spelled as the letter flow's bottom bar spells it
  // (TicketDetail's own „Noch n Felder offen") — one grammar for one fact across both roles.
  const gapNote = gapsOpen
    ? `Noch ${openCount} ${openCount === 1 ? 'Feld' : 'Felder'} offen`
    : undefined;
  // What Kopieren hands on. A fill-in letter's text is the ASSEMBLED one — the clerk's confirmed
  // values in place of the chips — never the served prose with its placeholders still in it.
  const copyText = hasSlots ? fill.assemble() : text;
  const headingRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);

  // ── The edit buffer ───────────────────────────────────
  // Local, and deliberately so: it is unconfirmed typing, and nothing above this component may
  // decide when it ends. The one thing the host owns is the in-flight flag, so a save survives
  // anything that rebuilds this subtree.
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  // The height the editor opens at — the BODY's own, measured the instant before it is replaced
  // (walk observation C, 2026-09-09). The editor is now in the card rather than on a stage, so a
  // fixed minimum would either shrink a long draft's card at the press or leave a short one's box
  // half empty. Taking the height the text already occupied means the swap trades one box for one
  // of exactly the same size, and only a body shorter than the floor grows at all — and that growth
  // is the press's own gesture.
  const [editBoxHeight, setEditBoxHeight] = useState(EDIT_BOX_MIN_HEIGHT);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // The rewrite the clerk has NOT saved and has NOT thrown away (fix round 1, 2026-09-09; narrowed
  // by the walk round). It existed because the card STAGE could be taken away under an open editor
  // by Escape or a backdrop click — neither a decision to discard a paragraph — and re-prefilling
  // from the saved text made it exactly that, silently and with no undo. No such destroyer exists
  // any more: the editor is the card, and the only exit that ends an edit without the clerk
  // choosing to is a STALE save. The words are kept for it, and handed back with their state
  // stated beside them.
  // Cleared by the two endings that ARE decisions — a save that landed, and Abbrechen — and by a
  // ticket swap, because a buffer belongs to the row it was typed against.
  const [unsaved, setUnsaved] = useState<string | null>(null);
  // A selection change ends the edit outright, buffer included. This card is keyed by KIND, so the
  // swap does NOT remount it — the same reply card simply receives the next vorgang's draft — and
  // an editor left standing across that would show one ticket's words in the box with another's
  // draft behind it, with Speichern writing the first into the second.
  useEffect(() => {
    setEditing(false);
    setEditError(null);
    setUnsaved(null);
  }, [ticketUuid]);
  // The row this card is CURRENTLY showing, readable from inside a promise continuation (round-2
  // review I1). The effect above cannot help a save that is already on the wire: `saveEdit` writes
  // state after an await, and with no remount to cancel it those writes land on whatever row is on
  // screen when the answer arrives. The observed sequence was a 409 answering after a selection
  // change and re-seeding the retained buffer, so the NEW ticket's card put up „Nicht gespeichert"
  // over a paragraph typed against the old one.
  // A ref, not the closure: the closure holds the row the press was made against (which is exactly
  // what we compare TO), and only a ref can say what the card is showing now.
  const liveTicketUuid = useRef(ticketUuid);
  useEffect(() => { liveTicketUuid.current = ticketUuid; }, [ticketUuid]);

  const beginEdit = () => {
    setEditText(unsaved ?? text);
    setEditError(null);
    setEditBoxHeight(Math.max(EDIT_BOX_MIN_HEIGHT, bodyRef.current?.offsetHeight ?? 0));
    setEditing(true);
  };
  // The two endings that are DECISIONS: a save that landed, and Abbrechen. Both discard the
  // retained buffer, because both of them mean the clerk is done with those words.
  const endEdit = () => {
    setEditing(false);
    setEditError(null);
    setUnsaved(null);
  };
  // Held for MY write or for somebody else's — the refusal is the same, only the sentence differs.
  const saveHeld = !!saving || !!otherSaving;
  const saveHeldReason = saving ? 'Speichern läuft' : otherSaving ? 'Aktion läuft' : undefined;
  // Kopieren answers to the SAME hold (round-3 review I1). A copy is a draft mutation — it claims
  // the slot and earns the row its approval credit — so it belongs inside the one-action-at-a-time
  // rule rather than beside it. Held by any draft action, its own copy included: that is also what
  // keeps a double-press from recording the claim twice.
  const copyHeld = !!saving || !!otherSaving;
  // A gap is the card's OWN reason to refuse, not a transient, so it dims at once rather than on
  // the worn-hold schedule — the same rule the bottom bar's slots follow.
  const copyRefused = copyHeld || gapsOpen;
  // Refused now, dimmed only once the hold is worn — one schedule for every held control on the
  // card, the same one the bottom bar's slots follow.
  const heldLooksHeld = !!holdVisible;
  const saveEdit = async () => {
    // Refused for either reason, and never only in the handler: the button says it is held, so a
    // press that quietly did nothing would be the button contradicting its own face.
    if (saveHeld) return;
    // Narrowing, not a second gate: the pencil that opens this editor is mounted for exactly these
    // kinds (see the header above), so this is the type system carrying the same fact into the
    // handler rather than a rule the clerk could ever meet.
    if (!isDraftEditableKind(slot.kind)) return;
    // The row this write goes against, captured BEFORE the await — every arm below compares it to
    // what the card is showing by the time the answer lands, and writes nothing if they differ.
    // Not an error path and not something to state: the clerk left, and the answer is simply about
    // a row that is no longer on screen. The host's own stale handling is unaffected — reloading
    // the row this card is no longer showing is still the right thing for the HOST to do.
    const pressedFor = ticketUuid;
    const rowMoved = () => liveTicketUuid.current !== pressedFor;
    const res = await onEditDraft?.(slot.kind, editText);
    if (!res || rowMoved()) return;
    // Success: 204 No Content, so there is nothing to paint from the answer — the host refetches
    // and this card's text and „bearbeitet" pill arrive with the new row.
    if (res.success) { endEdit(); return; }
    // The row moved on: the HOST reloads and says so in one toast. The editor closes — pressing
    // Speichern again would only lose the same race — but the words are RETAINED, because the
    // clerk did not choose to end this.
    if (res.stale) {
      setUnsaved(editText !== text ? editText : null);
      setEditing(false);
      setEditError(null);
      return;
    }
    // Everything else is a refusal with a sentence — „Bereits genehmigt.", „Entwurf wurde noch
    // nicht erzeugt" — which the clerk reads and acts on, with their own words still in the box.
    setEditError(res.error ?? 'Der Entwurf konnte nicht gespeichert werden.');
  };

  // Focus lands on the Kopieren button when there is one — the clerk composed this draft in order
  // to use it, so the next action is under the cursor. A card with no Kopieren (a host that cannot
  // record the credit) focuses its own heading instead, which is programmatically focusable for
  // exactly this reason: the point is that focus is INSIDE what arrived, never on <body>.
  useEffect(() => {
    if (!claimFocus) return;
    (copyRef.current ?? headingRef.current)?.focus();
    onFocusClaimed?.();
  }, [claimFocus, onFocusClaimed]);

  // The confirmation is a LABEL SWAP on the button that was pressed, inside a reserved width — the
  // clerk's eye is already there, and nothing on the footer row can move because of it. It rides
  // the host's answer, not the press: a clipboard the browser refused confirms nothing.
  const press = async () => {
    // Refused in the handler as well as on the face — a held button whose press still fired would
    // be the control contradicting what it says about itself. An open gap refuses here too: a
    // half-written letter on the clipboard is exactly what the note beside it warns about.
    if (copyRefused) return;
    const copiedToClipboard = await onCopyDraft?.(slot.kind, copyText);
    if (!copiedToClipboard) return;
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    setCopied(true);
    copiedTimer.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
  };
  // Filled navy in EVERY status (operator ruling 2026-09-08), green on confirmation. The letter
  // flow steps its own Kopieren back to a quiet outlined re-copy once the row is settled, because
  // there the clerk's next move is a download; a triage draft is grabbed by copying it whatever the
  // row's status, and dimming the card's only affordance after approval reads as an expired text.
  const lead = !copied;
  // Redaction tokens are the ONLY reason this text ever becomes markup: escape first (the house
  // plain-text treatment), then style the tokens into pills. A draft without a token stays a text
  // node — no parse, no chance of a draft's own angle brackets rendering as elements.
  const redacted = text.includes('[REDACTED_');
  // A settled slot is not rewritable — core answers „Bereits genehmigt." for it, and a COPY claim
  // counts (copy is the indirect approval). Said BEFORE the round trip rather than after it, so the
  // clerk does not type a paragraph into a refusal.
  const claimed = !!slot.claim;

  const card = (
    <div
      data-testid={`draft-card-${slot.kind}`}
      // No marginTop: the SlotSeparator inside the header IS this slot's gap, and the strip this
      // card may have replaced carried the same one. A margin here on top of it would make a card
      // 8px taller than the strip it replaced — the very shift the shared separator removes, just
      // smaller.
      style={{ animation: 'stx-fadeIn 0.3s ease' }}
    >
      {/* The ruled TWO-LINE header (2026-09-09, emptied by walk observation B the same day). Line 1
          used to be the vorgang; it was the DETAIL PANE's own h2 repeated once per card, so a
          two-draft row said the same sentence three times and none of the three added anything. The
          LINE stays — it is the space between whatever is above and this card, and the operator
          asked to keep exactly that — but it carries nothing. Line 2 is the kind, the only text in
          this header, with the card's own controls beside it. */}
      <div data-testid={`draft-header-${slot.kind}`} style={{ marginBottom: theme.spacing.xs }}>
        <SlotSeparator kind={slot.kind} />
        <div style={{
          display: 'flex', alignItems: 'center', gap: theme.spacing.xs,
          // Reserved: a host that wires no control leaves the kind standing here alone, and the
          // icons arriving must not change this line's height under it.
          minHeight: CARD_HEADER_LINE_HEIGHT,
        }}>
          <span
            ref={headingRef}
            // Programmatically focusable only (-1): never in the tab order, but a valid focus target
            // for the arriving-card handoff below. The class carries the ring that handoff needs —
            // :focus-visible does not fire for programmatic focus, so the global keyboard rule
            // cannot cover this one (index.css, .stx-draft-heading).
            tabIndex={-1}
            className="stx-draft-heading"
            // The flexible half — it yields to the icons, never the other way round, and it clips
            // rather than wrapping the header taller under them. „Weiterleitung an <Einheit>" is as
            // long as the tenant's routing target happens to be.
            style={{
              flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: theme.colors.navy,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {kindLabel}
          </span>
          <div
            data-testid={`draft-actions-${slot.kind}`}
            style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}
          >
            {/* The per-CARD Maximieren stood here until walk observation C (2026-09-09) removed it.
                Per-SECTION maximize is untouched and is where an enlarged view belongs: comparing
                the reply against the forward is the reason to enlarge anything, and a stage over
                ONE card cannot show that. Removed rather than hidden — see this file's header for
                what came off with it. */}
            {/* Bearbeiten left the header (2026-10-30): it sits after the letter, beside Kopieren. */}
          </div>
        </div>
      </div>
      {/* The BODY slot — one occupant at a time, in the card's own place in the walk. Bearbeiten
          swaps the letter box for a textarea right here (walk observation C, 2026-09-09): the card
          stage that used to carry the editor is gone, so the rewrite happens where the draft is
          read rather than on a surface over it. */}
      {editing ? (
        <>
          {/* The state of the box, on a line that is ALWAYS there so it cannot push the editor
              down when it fills. A retained rewrite differs from the draft it was opened on, and
              that is not something a clerk should have to infer by remembering. */}
          <div style={{ display: 'flex', alignItems: 'center', minHeight: 22 }}>
            {unsaved !== null && (
              <span data-testid={`draft-unsaved-${slot.kind}`} style={claimPillStyle}>
                {pillFace('info', 'nicht gespeichert')}
              </span>
            )}
          </div>
          {/* A draft that reaches this editor is PLAIN TEXT — a letter built from SEGMENTS is
              filled through its checklist instead and never opens this box (see the pencil's own
              gate above) — so it is edited in a textarea and never in the letter flow's
              contentEditable, which would write markup into a mail body. */}
          <textarea
            data-testid={`draft-edit-${slot.kind}`}
            aria-label={`Entwurf bearbeiten: ${kindLabel}`}
            value={editText}
            onChange={e => setEditText(e.target.value)}
            style={{
              ...letterBoxStyle,
              width: '100%', boxSizing: 'border-box',
              // The body's OWN height, measured at the press — see editBoxHeight. The swap trades
              // one box for a box of the same size; only a body shorter than the floor grows.
              minHeight: editBoxHeight,
              fontFamily: theme.font.family, resize: 'vertical',
              backgroundColor: theme.colors.white,
              border: `2px solid ${theme.colors.warning}`,
            }}
          />
          {editError && (
            <div
              data-testid={`draft-edit-error-${slot.kind}`}
              role="alert"
              style={{
                fontSize: 12, padding: '6px 12px', marginTop: theme.spacing.sm,
                borderRadius: theme.borderRadius.sm,
                color: theme.colors.error, backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
              }}
            >{editError}</div>
          )}
        </>
      ) : hasSlots ? (
        /* The letter with its gaps — the VNB pieces, mounted, not reimplemented: gap chips inside
         * the prose and the docked numbered checklist under it. No escalation chip and no zone
         * chip ride in with them (a wim row has neither), which leaves the editor's header row
         * reserved and empty — the same it already is on a VNB tenant whose zones are off. */
        <KlaerfallEditor
          segments={segments}
          marktprozess={marktprozess}
          draft={fill}
        />
      ) : redacted ? (
        <div
          ref={bodyRef}
          data-testid={`draft-body-${slot.kind}`}
          style={letterBoxStyle}
          dangerouslySetInnerHTML={{ __html: styleRedactionTokens(escapeHtmlText(text)) }}
        />
      ) : (
        <div ref={bodyRef} data-testid={`draft-body-${slot.kind}`} style={letterBoxStyle}>{text}</div>
      )}
      {/* Footer — mounted on every card at its fixed height, empty until a claim exists. What the
          clerk already did on the left, what they can do next on the right. */}
      <div data-testid={`draft-footer-${slot.kind}`} style={cardFooterStyle}>
        {claimKey && (
          <ServedFace family={claimKey.family} faceKey={claimKey.key} width={70} height={18}
            render={word => <span style={claimPillStyle}>{pillFace('info', word)}</span>} />
        )}
        {/* „abweichend" RETIRED (R32, 2026-09-18). The deviation belongs to the LIST ROW, where the
            filled classified handling stands beside the outlined past tense of the one the clerk
            actually approved — two served words, so the clerk reads WHAT deviated. Here it was a
            bare adjective in a footer that shows neither term of the comparison. `claim.disagreement`
            is still served and still typed (types.ts); nothing renders it. */}
        {/* The slot's OWN stamp (services/draftClaims folds it from the artifact that owns the
            slot), so one kind's rewrite can never light the pill on the kind beside it. */}
        {slot.editedAt && <ClerkTrace kind="bearbeitet" testId={`bearbeitet-badge-${slot.kind}`} />}
        {/* ONE right-hand affordance at a time (walk observation C, 2026-09-09): Kopieren, or —
            while this card is being rewritten — the save pair standing in its place. Kopieren is
            HIDDEN rather than held-and-dimmed here, because it would copy the SAVED text and its
            slot is exactly where the decision about the box now sits; the pair is what the clerk
            has to answer before there is anything to copy. */}
        {editing ? (
          <div
            data-testid={`draft-edit-actions-${slot.kind}`}
            style={{
              marginLeft: 'auto', display: 'flex', alignItems: 'center',
              gap: theme.spacing.xs, flexShrink: 0,
            }}
          >
            <button
              type="button"
              onClick={saveEdit}
              disabled={saveHeld}
              title={saveHeldReason}
              // Named per CARD, like Kopieren beside it: two cards can each carry a save pair, and
              // one accessible name across both would leave a screen-reader clerk unable to tell
              // which draft they were about to write.
              aria-label={`Speichern: ${kindLabel}`}
              style={cardButtonStyle(true, SAVE_BUTTON_WIDTH, saveHeld && heldLooksHeld)}
            >
              {/* The busy face is an icon inside a slot that is ALWAYS there, never a „Speichern…"
                  label swap: a label that grows mid-press moves the Abbrechen beside it.
                  aria-hidden: the Spinner carries its own „Lädt" label, and inside a named button
                  that label JOINS the button's accessible name — so the control a clerk had just
                  submitted renamed itself mid-press. A name must be as stable as a width;
                  `disabled` plus the German title already say the button is busy. */}
              <span aria-hidden="true" style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 12, height: 12, marginRight: theme.spacing.xs, verticalAlign: 'middle',
              }}>
                {saving ? <Spinner size={12} color={theme.colors.white} /> : null}
              </span>
              Speichern
            </button>
            {/* The QUIET half of the pair, and quiet is the card register's own outline — not the
                bottom bar's `off` face, which is the ruled NOT-CLICKABLE look and would say this
                live control cannot be pressed. It fades with THIS card's own save (nothing may be
                discarded out from under a write that is already on the wire) but NOT with another
                kind's: throwing a local buffer away needs no wire, so there is nothing for somebody
                else's request to conflict with. */}
            <button
              type="button"
              onClick={endEdit}
              disabled={saving}
              title={saving && holdVisible ? 'Speichern läuft' : undefined}
              aria-label={`Abbrechen: ${kindLabel}`}
              style={cardButtonStyle(false, CANCEL_BUTTON_WIDTH, !!saving && heldLooksHeld)}
            >Abbrechen</button>
          </div>
        ) : (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: theme.spacing.xs, flexShrink: 0 }}>
          {/* Bearbeiten after the letter, beside Kopieren — every role's one place for the letter
              verbs (2026-10-30). Only the kinds core lets a clerk rewrite, and never on a fill-in
              letter (its checklist IS its edit); disabled in place once the kind is approved. */}
          {onEditDraft && isDraftEditableKind(slot.kind) && !hasSlots && (
            <button
              type="button"
              onClick={beginEdit}
              disabled={claimed}
              title={claimed ? 'Bereits genehmigt' : 'Bearbeiten'}
              aria-label={`Bearbeiten: ${kindLabel}`}
              style={{
                ...cardButtonStyle(false, COPY_BUTTON_WIDTH, claimed),
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              }}
            ><PencilIcon size={11} />Bearbeiten</button>
          )}
          {onCopyDraft && (
          <button
            type="button"
            ref={copyRef}
            onClick={press}
            disabled={copyRefused}
            // The card's own reason leads: a clerk looking at an unfilled letter is told what is
            // missing, not that some other action happens to be on the wire. Stated in the TITLE
            // and nowhere else — a sentence rendered into the footer row would grow it as the last
            // gap closed, which is the shift this fixed-height footer exists to prevent.
            title={gapNote ?? (copyHeld ? 'Aktion läuft' : undefined)}
            aria-label={`Kopieren: ${kindLabel}`}
            style={{
              // The house held face: the same dim + not-allowed every other held control wears, so
              // "cannot be pressed right now" looks identical wherever a clerk meets it.
              ...cardButtonStyle(lead, COPY_BUTTON_WIDTH, (copyHeld && heldLooksHeld) || gapsOpen),
              // Two faces, not three: filled navy, and the green confirmation it swaps to for
              // 1.5 s (the card register's plain outline recoloured — same geometry, same reserved
              // width, so the label swaps inside a box that cannot resize). The letter flow's third
              // face (the quiet outlined re-copy) is deliberately absent here — see `lead` above.
              ...(copied ? {
                color: theme.colors.success,
                border: `1px solid ${theme.colors.success}`,
              } : null),
            }}
          >{copied ? 'Kopiert!' : 'Kopieren'}</button>
          )}
          </span>
        )}
      </div>
    </div>
  );

  return card;
};

// ─── An offered, not-yet-composed draft ─────────────────

/**
 * What a composition ANSWERED with — the text and, where the server describes them, its provenance
 * and its raw letter envelope. The shape the strip hands up and the area reveals a card from.
 */
interface ComposedDraft {
  text: string;
  renderSource: string | null;
  scaffoldJson: string | null;
}

// ─── The strip's two weights (R38 + board 3b A, part 5-2, 2026-10-02) ──────────────────────────

/**
 * Which LETTER strip the row LEADS with, said without a word: the bar's own idiom one level up —
 * solid brand navy for the kind the classifier recommends (the model's `leadKind`), a quiet outline
 * for the alternative. No marker word anywhere on a strip („KI-Vorschlag", „empfohlen",
 * „Alternative:" …): the Begründung's lean line already says it in words, and the strip only has to
 * agree with it at a glance. The hover names it for whoever asks; a screen reader, which sees
 * neither the weight nor a hover-only bubble, hears it as a visually-hidden suffix on the primary
 * strip's accessible name (STRIP_PRIMARY_A11Y_SUFFIX) — no VISIBLE word. Same 44px either way — a 1.5px
 * border inside a border-box of fixed height moves nothing.
 * Letters only: the receipt is a COMPANION to whatever the row leads with, never an alternative to
 * it (C3.6), so its strip bypasses this idiom and keeps its own companion face (review round 1).
 */
type StripVariant = 'primary' | 'secondary';

const STRIP_PRIMARY_HOVER = 'Vom Klassifizierer empfohlen';

// What a screen reader hears after the primary strip's kind (final review, m9): the border weight
// and the hover-only bubble are both invisible to it, so the accessible name carries the fact. The
// house's visually-hidden idiom (index.css .visually-hidden) — nothing on screen changes.
const STRIP_PRIMARY_A11Y_SUFFIX = ' – empfohlen';

const stripVariantStyle = (variant: StripVariant): React.CSSProperties => (variant === 'primary'
  ? { border: `1.5px solid ${theme.colors.navy}`, color: theme.colors.navy, fontWeight: 600 }
  : { border: `1px solid ${theme.colors.borderLight}`, color: theme.colors.textSecondary, fontWeight: 400 });

/** The hover is the candidate's own reason when the row served one (part 6, 2026-10-06) — on
 *  either weight; without a reason only the primary strip keeps the fixed recommendation text and a
 *  secondary strip is just the button. Hover-ONLY: the
 *  area hands focus to a strip programmatically (a compose that failed, a reveal), and a bubble on
 *  every such handoff would be noise — the strip's own text already names the kind. */
const withVariantHover = (variant: StripVariant | undefined, button: React.ReactElement, hoverText?: string) =>
  (hoverText ? <InfoTooltip text={hoverText} hoverOnly>{button}</InfoTooltip> : button);

const DraftKindStrip: React.FC<{
  kind: DraftSlotKind;
  /** A LETTER strip: primary (the row's lead kind) or secondary — see StripVariant. Absent for the
   *  receipt's offer strip (an older core's lazy receipt), which keeps its companion face. */
  variant?: StripVariant;
  /** The strip's hover (the candidate's reason, else the primary's fixed text); absent ⇒ none. */
  hoverText?: string;
  /** The tenant's served `draft_kind` vocabulary — see DraftKindCard's own `face` prop. */
  face: FaceFn;
  /** Named the same way the arriving card will be — the skeleton's own title line never
   * mismatches the heading that ends up replacing it. */
  targetUnit?: string | null;
  onRender: TriageDraftAreaProps['onRender'];
  /**
   * The composition landed — the area hands the focus to the card that is about to replace this,
   * and REVEALS that card from `composed` when the answer carried one (2026-09-17). `composed` is
   * null when the server answered success with no text: the card then arrives with the refetched
   * model, exactly as it always did.
   */
  onComposed: (kind: DraftSlotKind, composed: ComposedDraft | null) => void;
}> = ({ kind, variant, hoverText, face, targetUnit, onRender, onComposed }) => {
  // Reserve-at-press (Part C wave 2, 2026-09-09, adopted lean): pressing this SAME element no
  // longer spins in place — it grows, in the same tick as the click, into the card's own
  // letter-box canvas with a skeleton standing in it. `reserving` is that one flag; there is no
  // separate `busy` any more, because the skeleton IS the composing state.
  const [reserving, setReserving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The bound on the reserved wait — see STRIP_TIMEOUT_MS above. Cleared the moment reserving ends
  // for any OTHER reason (an explicit failure, a stale reload, or this component's own unmount
  // when the card actually lands), so it never fires a stale revert on top of one already done.
  const timeoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The re-press guard is a REF, not the `reserving` state: state commits are batched, and this
  // element is never disabled (see the button's own comment below), so the ref is the only thing
  // synchronous enough to refuse a second press before React has re-rendered the first one away.
  const inFlight = useRef(false);

  useEffect(() => () => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
    if (timeoutTimer.current) clearTimeout(timeoutTimer.current);
  }, []);

  // The one ending every non-success path shares: stand the offer back up, clear the bound, and —
  // for a genuine failure, never for a silent stale reload — state it in the live region for
  // STRIP_MESSAGE_MS before the offer quietly takes the box back over on its own.
  const revertToOffer = useCallback((failureText: string | null) => {
    inFlight.current = false;
    if (timeoutTimer.current) { clearTimeout(timeoutTimer.current); timeoutTimer.current = null; }
    setReserving(false);
    if (resetTimer.current) { clearTimeout(resetTimer.current); resetTimer.current = null; }
    if (failureText === null) { setMessage(null); return; }
    setMessage(failureText);
    resetTimer.current = setTimeout(() => setMessage(null), STRIP_MESSAGE_MS);
  }, []);

  const press = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setMessage(null);
    // Reserved IMMEDIATELY — same render tick as the click, before the `await` below ever runs.
    // A user-initiated growth must never wait on the network to become visible.
    setReserving(true);
    timeoutTimer.current = setTimeout(() => revertToOffer(REFUSAL_TEXT), STRIP_TIMEOUT_MS);
    const res = await onRender(kind);
    if (res.success) {
      // Deliberately still reserving: this component is UNMOUNTED by the card taking its place —
      // revealed from this very answer where it carried the text (the stateless receipt's only
      // path), otherwise by the host's refetch landing the slot. It never reverts on success; the
      // bound above is what saves an answer that brought neither.
      onComposed(kind, res.text ? {
        text: res.text,
        renderSource: res.renderSource ?? null,
        scaffoldJson: res.scaffoldJson ?? null,
      } : null);
      return;
    }
    // The row moved on: the HOST reloads it and says so in one toast. Standing the offer back up
    // silently is what the reloaded row will render against; a message here would be a second,
    // contradicting account of the same event.
    if (isStaleSuggestionError(res)) { revertToOffer(null); return; }
    revertToOffer(res.refused ? REFUSAL_TEXT : res.error ?? REFUSAL_TEXT);
  }, [kind, onRender, onComposed, revertToOffer]);

  const kindLabel = draftKindLabel(kind, face, targetUnit);
  const failed = message !== null;
  return (
    // The strip's own slot: the separator FIRST, so the line between this slot and whatever is
    // above it is standing before the press rather than arriving with the card (see SlotSeparator).
    <>
    <SlotSeparator kind={kind} />
    {withVariantHover(variant, <button
      type="button"
      data-testid={`draft-strip-${kind}`}
      data-variant={variant}
      onClick={press}
      // NEVER disabled — not while reserving, not after a failure. A disabled button loses focus,
      // and this ONE element is focused precisely when something is happening to it: at the press
      // (about to grow into the skeleton) and at the moment an outcome lands (the offer came back,
      // failed or not). The in-flight press is refused by the ref guard in press() instead, and
      // aria-busy is what says the reserving fact out loud; a failed offer stays pressable because
      // pressing again IS the retry.
      aria-busy={reserving}
      style={{
        // The ONE element for every state — offer, reserved skeleton, and failed — which is what
        // makes this the "same slot" reserve-at-press grows: nothing here ever unmounts on its
        // own, only the host swapping it for the arrived card does.
        // No marginTop of its own: the SlotSeparator above is this slot's gap, and it is the same
        // gap the card that replaces this will have. Two would make the strip taller than the card
        // it becomes, which is the shift this separator exists to remove.
        width: '100%', boxSizing: 'border-box',
        display: 'flex', flexDirection: reserving ? 'column' : 'row',
        alignItems: reserving ? 'stretch' : 'center', gap: theme.spacing.sm,
        height: reserving ? undefined : STRIP_HEIGHT,
        minHeight: reserving ? CARD_MIN_HEIGHT : STRIP_HEIGHT,
        padding: reserving ? theme.spacing.md : `0 ${theme.spacing.md}px`,
        textAlign: 'left',
        fontFamily: theme.font.family, fontSize: 13,
        backgroundColor: theme.colors.bgPage,
        ...(variant
          ? stripVariantStyle(variant)
          : { color: theme.colors.navyMid, border: `1px solid ${theme.colors.borderLight}` }),
        // A failure outranks the weight: the box states it in the error hue whichever strip it is.
        ...(failed ? { color: theme.colors.error, border: `1px solid ${theme.colors.error}` } : null),
        borderRadius: theme.borderRadius.md,
        cursor: reserving ? 'default' : 'pointer',
      }}
    >
      {reserving ? (
        <>
          {/* The kind title line — named the same as the card about to replace this, so a clerk
              reading the row mid-composition already knows which draft is on its way. */}
          <span style={{ fontSize: 13, fontWeight: 600, color: theme.colors.navy }}>{kindLabel}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
            <Spinner size={16} />
            <span>{RESERVE_LABEL}</span>
          </span>
          {/* The reserved letter box itself — a placeholder for the text that has not landed yet,
              claiming the SAME canvas the arriving card will fill (no second growth for a typical
              draft; one genuinely longer than one letter box still grows past this once it lands,
              which is the press's own gesture realized a beat later, not a second jump). */}
          <SkeletonBlock height={60} borderRadius={theme.borderRadius.md} />
        </>
      ) : !failed && (
        <>
          {/* The offer's wording lives in services/draftClaims with the kind's other three
              spellings (2026-09-08), so re-wording a kind is one file, not a grep. */}
          {/* The primary strip names its KIND; a never-composed secondary strip on a row that
              leads with a kind names the compose gesture with no marker word (the weight says
              which strip leads). */}
          <span style={{ flex: 1 }}>
            {variant === 'primary'
              ? draftKindLabel(kind, face, targetUnit)
              : draftKindOfferLabel(kind, face)}
            {variant === 'primary' && <span className="visually-hidden">{STRIP_PRIMARY_A11Y_SUFFIX}</span>}
          </span>
          <ChevronIcon />
        </>
      )}
      {/* The live region is mounted ALWAYS — for this ONE element's entire lifetime, through the
          reserving skeleton and back — empty until there is something to say. An aria-live element
          that arrives already carrying its text is generally NOT announced — the region has to be
          in the DOM before its content changes — which is exactly why it is hoisted here rather
          than nested inside the offer-only branch above: that branch is absent while reserving,
          and a region that came and went with it would be freshly created at the exact moment the
          failure text lands on it. */}
      <span
        data-testid={`draft-message-${kind}`}
        aria-live="polite"
        // A server sentence can be longer than the 44px box is wide. It stays on ONE line and
        // ellipsises rather than wrapping the box taller — the fixed height is the whole point of
        // the failed state, and the full text reaches the clerk through the live-region
        // announcement and the host toast beside it.
        style={{
          flex: !reserving && failed ? 1 : 0, minWidth: 0,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >{message ?? ''}</span>
    </button>, hoverText)}
    </>
  );
};

// ─── A served draft resting behind its companion strip (C3.6, ruled 2026-09-16) ─────────────────

/**
 * The receipt's RESTING shape: the text is already composed (services/draftClaims:
 * restsCollapsed) — the detail read handed it over, so pressing this needs no network call, no
 * skeleton and no timeout, only telling the area to show the card it is already holding. Same
 * slot, same gesture as every other strip (the layout law): the separator, the height, the
 * offer's own wording (`draftKindOfferLabel`) — a plain, synchronous state flip underneath it is
 * the only difference from `DraftKindStrip` above.
 *
 * It carries ONE thing the compose strip does not: the slot's CLAIM (2026-09-17). The receipt is
 * copy-only, so „Kopiert" is the whole record of whether the sender has been told — and a clerk
 * reading a row whose receipt rests collapsed would otherwise have to open it to find out. The
 * pill is the card's own (claimPillStyle / pillFace), so the same fact wears the same face
 * whichever of the two shapes the slot is in.
 */
const DraftKindCompanionStrip: React.FC<{
  kind: DraftSlotKind;
  /** A resting LETTER: primary (the row's lead kind) or secondary — see StripVariant. Absent for
   *  the receipt, which keeps its companion face and its own offer wording. */
  variant?: StripVariant;
  /** The strip's hover (the candidate's reason, else the primary's fixed text); absent ⇒ none. */
  hoverText?: string;
  /** The tenant's served `draft_kind` vocabulary — see DraftKindCard's own `face` prop. */
  face: FaceFn;
  /** Named the same way its card's heading is — read by the primary strip's label alone. */
  targetUnit?: string | null;
  claim: DraftClaim | null;
  onExpand: (kind: DraftSlotKind) => void;
}> = ({ kind, variant, hoverText, face, targetUnit, claim, onExpand }) => {
  const press = useCallback(() => onExpand(kind), [kind, onExpand]);
  // A resting letter EXISTS, so it wears its kind's name at either weight (board 3b — plain kind
  // words on both strips); the receipt keeps its companion sentence.
  const kindOffer = variant ? draftKindLabel(kind, face, targetUnit) : draftKindOfferLabel(kind, face);
  const claimKey = claimFaceKey(claim);
  return (
    <>
      <SlotSeparator kind={kind} />
      {withVariantHover(variant, <button
        type="button"
        data-testid={`draft-strip-${kind}`}
        data-variant={variant}
        onClick={press}
        style={{
          width: '100%', boxSizing: 'border-box',
          display: 'flex', flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm,
          height: STRIP_HEIGHT, minHeight: STRIP_HEIGHT,
          padding: `0 ${theme.spacing.md}px`,
          textAlign: 'left',
          fontFamily: theme.font.family, fontSize: 13,
          backgroundColor: theme.colors.bgPage,
          ...(variant
            ? stripVariantStyle(variant)
            : { color: theme.colors.navyMid, border: `1px solid ${theme.colors.borderLight}` }),
          borderRadius: theme.borderRadius.md,
          cursor: 'pointer',
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {kindOffer}
          {variant === 'primary' && <span className="visually-hidden">{STRIP_PRIMARY_A11Y_SUFFIX}</span>}
        </span>
        {claimKey && (
          <ServedFace family={claimKey.family} faceKey={claimKey.key} width={70} height={18}
            render={word => (
              <span data-testid={`draft-strip-claim-${kind}`} style={claimPillStyle}>
                {pillFace('info', word)}
              </span>
            )} />
        )}
        <ChevronIcon />
      </button>, hoverText)}
    </>
  );
};

// ─── The canvas, before the order can be trusted ────────

// The slot ORDER is served data (services/draftClaims: the proposal first, then the tenant's own
// primary-axis order), and during a session's genuine first /config fetch there is no served order
// to read — the fold falls back to the default layout, which on a tenant whose axis is served the
// other way round is the WRONG order. Painting cards then would mean re-ordering them on settle:
// a content shift, and the layout law forbids it outright. So the area shimmers its reserved
// canvas instead and mounts every card and strip in one go, already in the right order — the same
// posture the panel takes for its own vocabulary-derived chips, for the same reason.
// Reachable in practice, not hypothetical: a new tab has no sessionStorage vocabulary cache to
// paint from, and PopoutShell mounts a detail at boot.
const TriageDraftCanvasLoading: React.FC = () => (
  <div data-testid="triage-draft-canvas-loading" style={{ marginTop: theme.spacing.sm }}>
    <SkeletonBlock height={22} width={180} borderRadius={4} />
    <SkeletonBlock height={96} borderRadius={theme.borderRadius.md} style={{ marginTop: theme.spacing.xs }} />
  </div>
);

// ─── The area ───────────────────────────────────────────

const TriageDraftArea: React.FC<TriageDraftAreaProps> = ({
  ticket, model, onRender, onCopyDraft, shownKind, onShowKind, onEditDraft,
  busyKind, holdVisible,
}) => {
  const triage = ticket.suggestion?.triage;
  // Read for the ORDER's sake alone — this surface resolves no label of its own (the panel below
  // does its own resolving). See TriageDraftCanvasLoading above for why a shimmer, not cards.
  const cm = useCategoryMeta();
  // The kind whose strip was just pressed, held until its card actually arrives — the handoff is
  // driven by the MODEL gaining that kind, not by the press, because the card only exists once the
  // host has refetched. A composition that never lands simply leaves this set and focuses nothing.
  const [awaitedKind, setAwaitedKind] = useState<DraftSlotKind | null>(null);
  // The row's fills belong to the SUGGESTION attempt, exactly as the letter flow's own do — a
  // reanalysis is a new attempt under the same uuid and opens a clean letter. Each card extends
  // this with its own kind, so one kind's fill can never be read into the kind beside it.
  const draftKeyBase = ticket.suggestion?.id != null ? `sug-${ticket.suggestion.id}` : ticket.uuid;
  // A draft this surface composed and is SHOWING from the answer itself (2026-09-17), until a read
  // supersedes it. Keyed by the suggestion ATTEMPT for the same reason the fills are: a reanalysis
  // is a different letter, and the previous attempt's text may not be read into it. It exists
  // because the receipt is stateless — the composition is never written as a draft row, so there
  // is no later read that would bring it and a surface waiting for one waits forever.
  const [revealed, setRevealed] = useState<
    { key: string; kind: DraftSlotKind; text: string; renderSource: string | null; scaffold: ScaffoldData | null }
    | null
  >(null);
  const handleComposed = useCallback((kind: DraftSlotKind, composed: ComposedDraft | null) => {
    setAwaitedKind(kind);
    // A composed draft is DISCLOSED, exactly as a pressed companion strip is (Q2 B, 2026-09-17):
    // one meaning of "expanded" for every strip kind, so a kind that happens to be backed by a
    // draft row and one that is composed fresh by the read behave the same on the next visit.
    setDisclosed(ticket.uuid, kind, true);
    if (!composed) return;
    setRevealed({
      key: draftKeyBase,
      kind,
      text: composed.text,
      renderSource: composed.renderSource,
      // The same parser the fold uses — a revealed letter and a served one are one shape.
      scaffold: parseScaffoldJson(composed.scaffoldJson),
    });
  }, [draftKeyBase, ticket.uuid]);
  const releaseFocus = useCallback(() => setAwaitedKind(null), []);
  // The revealed draft is THIS attempt's and only until the model carries the slot itself.
  const revealedHere = revealed && revealed.key === draftKeyBase ? revealed : null;
  // Which strips the clerk has DISCLOSED — expanded into their card. Not this component's state
  // any more (Q2 B, ruled 2026-09-17): it is the ticket's, held by services/draftDisclosure for
  // the browser session and shared with every other window. Attempt-keying is gone with it — a
  // reanalysis is a new letter but the same ticket, and "show me the receipt" was said about the
  // ticket. This surface only mirrors the store so React re-renders when it changes.
  const [expandedHere, setExpandedHere] = useState<ReadonlySet<DraftSlotKind>>(
    () => readDisclosed(ticket.uuid));
  useEffect(() => {
    setExpandedHere(readDisclosed(ticket.uuid));
    return onDisclosureChanged((uuid) => {
      if (uuid === ticket.uuid) setExpandedHere(readDisclosed(ticket.uuid));
    });
  }, [ticket.uuid]);
  const expandCompanion = useCallback((kind: DraftSlotKind) => {
    setDisclosed(ticket.uuid, kind, true);
    if (isDraftDecisionKind(kind)) onShowKind?.(kind);
    // Same focus handoff as a composed card's arrival — see `arrived` below.
    setAwaitedKind(kind);
  }, [ticket.uuid, onShowKind]);

  const arrived = awaitedKind !== null
    && (
      model.rendered.some(slot => slot.kind === awaitedKind)
      || revealedHere?.kind === awaitedKind
      || expandedHere.has(awaitedKind)
    )
    ? awaitedKind
    : null;
  // Hooks before the early return (rules of hooks) — a row with no triage record is not this
  // component's row at all.
  if (!triage) return null;

  const offersStrips = isTriageDecidable(ticket.status);
  // The row's slots as this surface actually SHOWS them: the served slot always, plus a draft this
  // surface composed and is holding from the render answer itself (see `revealed` — the stateless
  // receipt's only path, and the older-core fallback's). Resolved ONCE here and read by both the
  // switcher and the walk: a tab still offering „· erzeugen" for a letter standing in the column
  // beside it is the two of them disagreeing about the same slot.
  const resolvedSlots: DraftSlot[] = model.slots.map(slot => (
    slot.text === null && revealedHere?.kind === slot.kind
      ? {
        ...slot,
        text: revealedHere.text,
        renderSource: revealedHere.renderSource,
        scaffold: revealedHere.scaffold,
      }
      : slot));
  return (
    <div data-testid="triage-draft-area" style={{
      // The canvas a composed draft occupies, reserved from the first paint — the same ~140px the
      // generating overlay paints on, so the section neither grows nor jumps when drafts land.
      //
      // NOT on shape 'none' (eyeball round 4, ruled 2026-09-18): that row offers no slot, carries
      // no artifact and can never be given one, so there is nothing for the reservation to catch —
      // it was simply blank space under the triage panel, most visibly on a Zur-Kenntnis row in the
      // pop-out. Reserving space for a thing that cannot arrive is not layout stability, it is the
      // cost of it with none of the benefit. Kept for 'drafts', 'refusal' and 'legacy', each of
      // which does put something in that canvas.
      minHeight: model.shape === 'none' ? undefined : CARD_MIN_HEIGHT,
    }}>
      {cm.configLoading ? <TriageDraftCanvasLoading /> : (
        <>
          {/* A REFUSED row (the composer was asked for the primary and returned nothing) states the
              refusal in the refused slot's own place, in the strip's own refusal wording — with or
              without a Begründung above it: the block says why the mail landed here, never that a
              draft failed (review round 1). Not pressable: the retry is the bar's Neu analysieren. */}
          {model.shape === 'refusal' && (() => {
            const refused = model.slots.find(slot => slot.posture === 'primary' && slot.text === null);
            return (
              <div
                data-testid={`draft-refusal-${refused?.kind ?? 'primary'}`}
                role="status"
                style={{
                  marginTop: theme.spacing.sm, boxSizing: 'border-box',
                  display: 'flex', alignItems: 'center', height: STRIP_HEIGHT,
                  padding: `0 ${theme.spacing.md}px`, fontSize: 13,
                  color: theme.colors.error, backgroundColor: theme.colors.bgPage,
                  border: `1px solid ${theme.colors.error}`, borderRadius: theme.borderRadius.md,
                }}
              >{REFUSAL_TEXT}</div>
            );
          })()}
          {/* An older row with nothing to show and no Begründung states it in ONE plain line — the
              same line the letter flow draws for its own old no-draft rows. */}
          {model.shape === 'legacy' && !ticket.suggestion?.reasoning && (
            <p data-testid="no-draft-line" style={{ margin: `${theme.spacing.sm}px 0 0`, fontSize: 13, color: theme.colors.textSecondary }}>
              Kein Entwurf vorhanden
            </p>
          )}

      {/* ONE ordered walk over the slots — in the MODEL's order (services/draftClaims: the
          proposal first, then the served primary-axis order) — not a list of cards followed by a
          list of strips (operator ruling 2026-09-08). Two separate lists let whichever kind was
          composed first jump above the other kind's strip and be pushed back down when that one
          arrived: on a row that offers BOTH kinds the clerk watched the two things trade places
          twice. Each slot now renders IN ITS OWN PLACE — its card if it carries text, else its
          strip if it is still an offer this status may act on, else nothing — so a slot's position
          is fixed for the row whatever each of them happens to be at the time. This surface holds
          NO opinion about the order itself; re-ordering is a change to the model alone. */}
          <div
            data-testid="triage-draft-slots"
          >
            {model.shape === 'drafts' && resolvedSlots.map(slot => {
              // The strip's hover (part 6, 2026-10-06): the ranked candidate's reason for this
              // kind's handling when the row served one; else the primary keeps its fixed text.
              const stripHoverFor = (kind: DraftSlotKind): string | undefined =>
                candidateForKind(ticket.suggestion, cm.decisionKindHandling, kind)?.reason
                ?? (kind !== 'ack' && kind === model.leadKind ? STRIP_PRIMARY_HOVER : undefined);
              // A slot resting collapsed (the receipt: services/draftClaims restsCollapsed)
              // carries real text but stays behind its companion strip until THIS surface's own
              // local disclosure says otherwise — never gated on `offersStrips`, because
              // expanding it composes nothing and claims nothing; it only shows what the read
              // already handed over.
              const collapsed = !!slot.restsCollapsed && slot.text !== null
                && !expandedHere.has(slot.kind);
              // `slot` is already the resolved one (served text first, then anything this surface
              // is holding from a render answer), so the only question left here is whether it
              // rests behind its companion strip.
              const shown = collapsed ? null : slot.text !== null ? slot : null;
              const box = shown !== null ? (
                <DraftKindCard
                  slot={shown}
                  face={cm.face}
                  ticketUuid={ticket.uuid}
                  draftKeyBase={draftKeyBase}
                  // The served handling — read by the gap chips alone, for their hue.
                  marktprozess={ticket.primaryAxis ?? ''}
                  targetUnit={triage.routingTargetUnit}
                  onCopyDraft={onCopyDraft}
                  claimFocus={slot.kind === arrived}
                  onFocusClaimed={releaseFocus}
                  // Bearbeiten is a WRITE affordance, so it obeys the rule the offer strips already
                  // do: only the statuses where a triage decision still exists carry one. The
                  // Archiv shows the same cards, and rewriting a draft on a settled ticket is the
                  // same category of mistake as composing another one there.
                  onEditDraft={offersStrips ? onEditDraft : undefined}
                  saving={busyKind === slot.kind}
                  // Any draft write that is NOT this slot's — see DraftKindCard's otherSaving.
                  otherSaving={busyKind != null && busyKind !== slot.kind}
                  holdVisible={holdVisible}
                />
              ) : collapsed ? (
                <DraftKindCompanionStrip
                  kind={slot.kind}
                  variant={slot.kind === 'ack' ? undefined : slot.kind === model.leadKind ? 'primary' : 'secondary'}
                  hoverText={stripHoverFor(slot.kind)}
                  face={cm.face}
                  targetUnit={triage.routingTargetUnit}
                  claim={slot.claim}
                  onExpand={expandCompanion}
                />
              ) : offersStrips && slot.posture === 'lazy' ? (
                <DraftKindStrip
                  kind={slot.kind}
                  variant={slot.kind === 'ack' ? undefined : slot.kind === model.leadKind ? 'primary' : 'secondary'}
                  hoverText={stripHoverFor(slot.kind)}
                  face={cm.face}
                  targetUnit={triage.routingTargetUnit}
                  onRender={onRender}
                  onComposed={handleComposed}
                />
              ) : null;
              // A slot that paints nothing renders NOTHING — not an empty wrapper.
              if (box === null) return null;
              // A composed decision letter other than the one the host shows is hidden, never
              // unmounted: its fill state and edit buffer survive the switch. Strips stay.
              const hiddenLetter = !!shownKind && shown !== null && isDraftDecisionKind(slot.kind) && slot.kind !== shownKind;
              return (
                <div
                  key={slot.kind}
                  data-testid={`draft-slot-${slot.kind}`}
                  hidden={hiddenLetter}
                  style={{ minWidth: 0 }}
                >
                  {box}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default TriageDraftArea;
